import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Modal from 'react-bootstrap/Modal'
import { FaInfoCircle } from "react-icons/fa";
import Timer from "../../pageInside/timer";
import {authenticate, UserAgree, UserDecline, getElakayLang } from "../../auth";
import RoleDocumentContent from "../../pageInside/roleDocuments/RoleDocumentContent";
import useRoleDocuments from "../../pageInside/roleDocuments/useRoleDocuments";
import {
    DOCUMENT_STATUS,
    buildRoleOptions,
    canonicalRoleId,
    getReadableRoleIds,
    getRoleLevel,
    resolveViewedRole,
    roleDocumentsText,
    toTimestamp,
} from "../../../utils/roleDocuments";
import "../../pages/addProduct.css"
import "../../pageInside/roleDocuments/roleDocuments.css"

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["actPageAssUserExit"]?.[key] || fallback;

const isNullOrEmpty = (value)=> {
    if (value == null || value === false) return true;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}

const STEP_PLAN = 'plan'
const STEP_TERM = 'term'
const STEP_AGREE = 'agree'
const STEPS = [STEP_PLAN, STEP_TERM, STEP_AGREE]

// Role proposal / role documents modal, opened from the Footer (an admin
// proposed a new role to this signed-in account: ActiveAgree) and from
// admin_associate (read the documents of the account's own role).
//
// Three role notions, never mixed:
//   currentRole  the account's real role (userActiveRole.roleName) -- reading never changes it
//   invitedRole  the pending proposal (userActiveRole.userActiveRole.roleName), decided server-side
//   viewedRole   the role whose documents are displayed -- presentation state only
// Accept/decline always act on the proposal stored server-side
// (POST /user/user_agree, /user/user_decline): the viewed role is never sent.
const  UserExitActAss = (props) => {

    const {
        showProposition,
        setShowProposition,
        infoSource,
        token,
        userActiveRole,
        ActiveAgree = false
    }= props
    __setElakayInfoSource(infoSource);
    const t = (key, fallback, values) => roleDocumentsText(infoSource, key, fallback, values)

    const location = useLocation();
    const lang = getElakayLang()?.obj?.code || 'en'

    const proposal = !isNullOrEmpty(userActiveRole?.userActiveRole) ? userActiveRole.userActiveRole : null
    const currentRole = canonicalRoleId(userActiveRole?.roleName)
    const invitedRole = canonicalRoleId(proposal?.roleName)
    // The role this modal is about: the proposal when there is one,
    // otherwise the account's own role (admin_associate).
    const primaryRole = invitedRole || currentRole || 'Royalty'
    const expiresAt = proposal ? toTimestamp(proposal.experyDate) : null
    // Never more than the higher of the real role and the offered role
    // (outside a proposal: the account's own level, as before).
    const maxLevel = Math.max(getRoleLevel(currentRole), getRoleLevel(invitedRole))

    const roleOptions = buildRoleOptions((roleId, index) => __elakayText(`t00${index + 1}`, roleId))

    const [selectedRole, setSelectedRole] = useState(null)
    const [step, setStep] = useState(STEP_PLAN)
    const [timerExpired, setTimerExpired] = useState(false)
    const [interesting, setInteresting] = useState(false);
    const [interestingAgree, setInterestingAgree] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [planAfCh, setPlanAfCh] = useState(false);
    const [ternConCh, setTernConCh] = useState(false);
    const [agreeCh, setAgreeCh] = useState(false);
    const docRef = useRef(null);

    const requestedFromUrl = useMemo(() => new URLSearchParams(location.search).get("r"), [location.search])

    // Read access (readable roles, invitation status) -- computed server-side.
    const docs = useRoleDocuments({
        authToken: token,
        lang,
        viewedRole: null,
        enabled: Boolean(showProposition),
    })

    const readableRoleIds = useMemo(() => {
        const fromAccess = getReadableRoleIds(docs.accessStatus === DOCUMENT_STATUS.SUCCESS ? docs.access : null, maxLevel)
        return fromAccess.filter((roleId) => getRoleLevel(roleId) <= Math.max(maxLevel, 1))
    }, [docs.access, docs.accessStatus, maxLevel])

    const viewedRole = resolveViewedRole({
        requestedRole: selectedRole,
        invitedRole: primaryRole,
        readableRoleIds,
    }) || primaryRole

    // The three documents of the viewed role.
    const viewed = useRoleDocuments({
        authToken: token,
        lang,
        viewedRole,
        enabled: Boolean(showProposition && viewedRole),
        loadAccess: false,
    })

    // A different proposal (or account) is a different invitation: start over.
    const invitationKey = `${currentRole || ''}|${invitedRole || ''}|${expiresAt || ''}`
    useEffect(() => {
        setSelectedRole(null)
        setStep(STEP_PLAN)
        setTimerExpired(false)
        setPlanAfCh(false)
        setTernConCh(false)
        setAgreeCh(false)
        setInteresting(false)
        setInterestingAgree(false)
        setSubmitError("")
    }, [invitationKey])

    // "?r=<Role>" pre-selects a role to read -- only honored when readable
    // (resolveViewedRole ignores anything else; the backend refuses it too).
    useEffect(() => {
        if (requestedFromUrl) setSelectedRole(canonicalRoleId(requestedFromUrl))
    }, [requestedFromUrl])

    const scrollToDocument = () => {
        if (docRef.current?.scrollIntoView) docRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const goToStep = (nextStep) => {
        setStep(nextStep)
        scrollToDocument()
    }

    // Reading another role's documents: back to its Business Plan. The
    // invitation and the account's role are untouched, and confirmations
    // given for the invited role stay attached to it.
    const handleUserRole = (event) => {
        const roleId = canonicalRoleId(event.target.value)
        if (!roleId || !readableRoleIds.includes(roleId)) return
        setSelectedRole(roleId)
        setStep(STEP_PLAN)
    }

    const invitationExpired = Boolean(proposal) && (
        timerExpired ||
        (expiresAt !== null && expiresAt <= Date.now()) ||
        viewed.access?.invitationStatus === 'expired' ||
        docs.access?.invitationStatus === 'expired'
    )

    // POST /user/user_agree applies the proposal stored server-side; the
    // viewed role is never sent.
    const clickAgree = () => {
        if (invitationExpired) return
        setSubmitError("")
        setSubmitting(true)
        UserAgree(token).then(data => {
            if (data.error) {
                setSubmitError(data.error)
            } else {
                authenticate(data, () => {});
            }
        }).catch((err) => {
            setSubmitError(err.message)
        }).finally(() => {
            setSubmitting(false)
        })
    }

    // POST /user/user_decline only withdraws the pending proposal, never
    // touching the account's current role/agreeRole.
    const clickDecline = () => {
        setSubmitError("")
        setSubmitting(true)
        UserDecline(token).then(data => {
            if (data.error) {
                setSubmitError(data.error)
            } else {
                authenticate(data, () => {});
            }
        }).catch((err) => {
            setSubmitError(err.message)
        }).finally(() => {
            setSubmitting(false)
        })
    }

    const CheckInteresting = ()=>{
        setInteresting(prev => !prev)
        setPlanAfCh(false)
        setTernConCh(false)
        setAgreeCh(false)
    }
    const CheckInterestingAgree = ()=>{
        setInterestingAgree(prev => !prev)
        setPlanAfCh(false)
        setTernConCh(false)
        setAgreeCh(false)
    }
    const toggleAgreement = (setter) => () => {
        setter(prev => !prev)
        setInteresting(false)
        setInterestingAgree(false)
    }

    const roleLabel = (roleId) => roleOptions.find((role) => role.id === roleId)?.label || roleId || ''
    const viewingPrimary = viewedRole === primaryRole
    const canAccept = ActiveAgree && planAfCh && ternConCh && agreeCh && !submitting && !invitationExpired
    const canDecline = interesting && interestingAgree && !submitting

    // Read-only mode (admin_associate, role already held): the boxes show
    // the role as already agreed and can't be toggled.
    const readOnlyChecked = !ActiveAgree

    const agreementControls = () => (
        <div className="role-proposal-agree">
            {submitError && <div className="alert alert-danger" role="alert">{submitError}</div>}
            {invitationExpired && (
                <div className="role-doc-notice role-doc-notice--warning" role="status">
                    <FaInfoCircle aria-hidden="true" />
                    <span>{t("expiredActions", "This invitation has expired. Actions that require a valid invitation are disabled.")}</span>
                </div>
            )}
            <ul className="list-unstyled mt-3">
                <li>
                    <input
                        onChange={CheckInteresting}
                        type="checkbox"
                        className="form-check-input"
                        checked={interesting}
                        id="notInterested"
                    />
                    <label className="form-check-label" htmlFor="notInterested">
                        {infoSource.language(infoSource, "label", "notInterested")}
                    </label>
                    {interesting && <ul className="list-unstyled ms-4 mt-2">
                        <li>
                            <input
                                onChange={CheckInterestingAgree}
                                type="checkbox"
                                className="form-check-input"
                                checked={interestingAgree}
                                id="notInterestedAgree"
                            />
                            <label className="form-check-label" htmlFor="notInterestedAgree">
                                {infoSource.language(infoSource, "title", "chButAgree")}
                            </label>
                        </li>
                        <li className="mt-2">
                            <button type="button" onClick={clickDecline} disabled={!canDecline} className="btn btn-primary">
                                {infoSource.language(infoSource, "Button", "submit")}
                            </button>
                        </li>
                    </ul>}
                </li>
            </ul>
            <ul className="list-unstyled">
                <li>
                    <input
                        onChange={toggleAgreement(setPlanAfCh)}
                        type="checkbox"
                        className="form-check-input"
                        checked={readOnlyChecked || planAfCh}
                        disabled={readOnlyChecked || invitationExpired}
                        id="planAffairLu"
                    />
                    <label className="form-check-label" htmlFor="planAffairLu">
                        {infoSource.language(infoSource, "label", "planAffairLu")}
                    </label>
                </li>
                <li>
                    <input
                        onChange={toggleAgreement(setTernConCh)}
                        type="checkbox"
                        className="form-check-input"
                        checked={readOnlyChecked || ternConCh}
                        disabled={readOnlyChecked || invitationExpired}
                        id="termConditionAgre"
                    />
                    <label className="form-check-label" htmlFor="termConditionAgre">
                        {infoSource.language(infoSource, "label", "termConditionAgre")}
                    </label>
                </li>
                <li>
                    <input
                        onChange={toggleAgreement(setAgreeCh)}
                        type="checkbox"
                        className="form-check-input"
                        checked={readOnlyChecked || agreeCh}
                        disabled={readOnlyChecked || invitationExpired}
                        id="acpContinue"
                    />
                    <label className="form-check-label" htmlFor="acpContinue">
                        {infoSource.language(infoSource, "title", "chButAgree")}{' '}
                        {infoSource.language(infoSource, "label", "acpContinue")}
                    </label>
                </li>
            </ul>
            {ActiveAgree && (
                <div className="d-flex justify-content-end">
                    <button type="button" onClick={clickAgree} disabled={!canAccept} className="btn btn-primary">
                        {infoSource.language(infoSource, "Button", "actUser")}
                    </button>
                </div>
            )}
        </div>
    )

    const stepLabels = {
        [STEP_PLAN]: infoSource.language(infoSource, "label", "planAffai"),
        [STEP_TERM]: infoSource.language(infoSource, "label", "termCondi"),
        [STEP_AGREE]: infoSource.language(infoSource, "label", "FinalAgree"),
    }
    const stepIndex = STEPS.indexOf(step)
    const previousStep = STEPS[stepIndex - 1]
    const nextStep = STEPS[stepIndex + 1]

    const toolbar = () => (
        <div className="role-proposal-toolbar">
            <div className="role-proposal-select">
                <label htmlFor="role-proposal-viewed-role">{t("viewRole", "Role to read")}</label>
                <select
                    id="role-proposal-viewed-role"
                    value={viewedRole}
                    onChange={handleUserRole}
                    className="form-select"
                    aria-describedby="role-proposal-read-only-hint"
                >
                    {roleOptions.filter((role) => readableRoleIds.includes(role.id)).map((role) => (
                        <option key={role.id} value={role.id}>{role.label}</option>
                    ))}
                </select>
                <div className="role-proposal-meta">
                    {currentRole && <span>{t("currentRole", "Current role")}: <strong>{roleLabel(currentRole)}</strong></span>}
                    {invitedRole && <span>{t("invitedRole", "Invited role")}: <strong>{roleLabel(invitedRole)}</strong></span>}
                </div>
            </div>
            {proposal && expiresAt !== null && (
                <div className="role-proposal-timer">
                    <Timer
                        infoSource={infoSource}
                        enDate={expiresAt}
                        userExit={true}
                        onExpire={() => setTimerExpired(true)}
                    />
                </div>
            )}
            <ol className="role-proposal-steps" aria-label={t("steps", "Documents")}>
                {STEPS.map((stepId, index) => (
                    <li key={stepId} className={step === stepId ? 'active' : index < stepIndex ? 'done' : ''}>
                        <button type="button" onClick={() => goToStep(stepId)} aria-current={step === stepId ? 'step' : undefined}>
                            {stepLabels[stepId]}
                        </button>
                    </li>
                ))}
            </ol>
            {!viewingPrimary && (
                <p className="role-doc-notice" id="role-proposal-read-only-hint" style={{ gridColumn: '1 / -1' }}>
                    <FaInfoCircle aria-hidden="true" />
                    <span>{t(
                        invitedRole ? "viewingNotice" : "viewingNoticeOwnRole",
                        invitedRole
                            ? "You are reading the {viewed} documents. Your invitation remains: {invited}."
                            : "You are reading the {viewed} documents. Your role remains: {invited}.",
                        { viewed: roleLabel(viewedRole), invited: roleLabel(primaryRole) }
                    )}</span>
                </p>
            )}
        </div>
    )

    const documentSection = () => (
        <div className="role-proposal-document" ref={docRef}>
            <RoleDocumentContent
                infoSource={infoSource}
                status={viewed.statuses[step]}
                document={viewed.documents[step]}
                errorCode={viewed.error?.code}
                onRetry={viewed.retry}
            />
            {step === STEP_AGREE && (viewingPrimary ? agreementControls() : (
                <div className="role-proposal-agree">
                    <p className="role-doc-notice">
                        <FaInfoCircle aria-hidden="true" />
                        <span>{t("acceptOnlyInvited", "To accept, open the Final Agreement of {invited}.", { invited: roleLabel(primaryRole) })}</span>
                    </p>
                    <button type="button" className="btn btn-outline-primary mt-2" onClick={() => { setSelectedRole(primaryRole); setStep(STEP_AGREE) }}>
                        {roleLabel(primaryRole)}
                    </button>
                </div>
            ))}
        </div>
    )

    return (
        <Modal
            show={showProposition}
            onHide={() => setShowProposition(false)}
            dialogClassName="role-proposal-modal"
            aria-labelledby="role-proposal-modal-title"
            centered
            scrollable={false}
        >
            <Modal.Header closeButton>
                <Modal.Title id="role-proposal-modal-title">
                    <h2>{infoSource.language(infoSource, "label", "allKnowAbout")}{' '}<span>{roleLabel(viewedRole)}</span></h2>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {toolbar()}
                {documentSection()}
                <div className="role-proposal-footer">
                    <span>
                        {previousStep && (
                            <button type="button" onClick={() => goToStep(previousStep)} className="btn btn-primary">
                                {infoSource.laguangeElakay('Button', infoSource.languageAPI).previous}
                            </button>
                        )}
                    </span>
                    <span>
                        {nextStep && (
                            <button type="button" onClick={() => goToStep(nextStep)} className="btn btn-primary">
                                {infoSource.laguangeElakay('Button', infoSource.languageAPI).next}
                            </button>
                        )}
                    </span>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default UserExitActAss;
